import { BaseServerConnector, ProtocolArray } from './BaseServerConnector.js';
import { ConnectorServerConfig } from '../servers/ConnectorServer.js';
import { PROTOCOLS, WEBRTC_RELIABLE_CHANNEL, ensureArrayBuffer } from '@gotti/core';
import { WebRTCClient } from './WebRTCClient.js';
import { WebSocketServer, WebSocket } from 'ws';
import { parse } from 'url';

export type WebRTCClientOptions = {
  webRtcClient: WebRTCClient,
  closed?: boolean,
  gatheringTimeout?: NodeJS.Timeout,
  pingInterval?: NodeJS.Timeout,
  noPingReceivedTimeout?: NodeJS.Timeout,
  messageHandler?: (msg: ProtocolArray) => void,
}


const DEFAULT_PING_TIMEOUT = 5000;
const PING_STRINGED = PROTOCOLS.PING.toString();
export class WebRTCServerConnector extends BaseServerConnector<WebRTCClientOptions> {
  private wsServer!: WebSocketServer;
  private awaitingInits : {[privateId: string]: { ws: WebSocket, timeout: NodeJS.Timeout, publicId: string }} = {};
  constructor(c: ConnectorServerConfig) {
    super(c);
  }

  public start() {
    // this.logger.info('websocket options was', this.config.options.websocketOptions)
    const websocketOptions = this.config.options.websocketOptions;
    this.wsServer = new WebSocketServer({ ...websocketOptions, host: this.listenHost(websocketOptions?.host) });
    // console.log('made ws server');
    this.wsServer.on('connection', (ws: WebSocket, req: any) => {
        try {
            const { privateId, publicId } = parse(req.url, true).query;
            if(!privateId || !publicId || typeof privateId !== 'string' || typeof publicId !== 'string') {
                throw new Error(`No ids given`);
            }
            if(this._onValidateClient(privateId, publicId)) {
                this.awaitingInits[privateId] = {
                  publicId,
                  ws,
                  timeout: setTimeout(() => {
                    delete this.awaitingInits[privateId];
                    if(this.clientsByPrivateId[privateId]) {
                      console.error(`Timed out waiting for init of player: ${privateId}`);
                      this.removeClient(privateId);
                    }
                  }, this.config.options.clientTimeout || 15000)
                }
                this.init(privateId, publicId);
                const client = this.clientsByPrivateId[privateId];
                client.options.webRtcClient.on('added-signal', (signalData: any) => {
                  ws.send(JSON.stringify([PROTOCOLS.PEER_SIGNAL_REQUEST, signalData]))
                })

                ws.on('message', (data) => {
                  const payload = JSON.parse(data.toString());
                  switch(payload[0]) {
                    case PROTOCOLS.PEER_SIGNAL_REQUEST:
                      this.clientsByPrivateId[privateId].options.webRtcClient.receivedRemoteSignal(payload[1])
                      break;
                  }
                })
                ws.on('close', () => {
                  // console.log('SERVER: WS CLOSED')
                  if(this.awaitingInits[privateId]) {
                    this.removeClient(privateId);
                  }
                })
            } else {
                throw new Error(`Could not validate client`)
            }
        } catch (err) {
            ws.close();
        }
    });
    this.logger.info(`started webrtc connector server- listening for connection handshakes on websocket port: ${this.config.options.websocketOptions?.port}`);
  }
  
  public init(privateId: string, publicId: string) {
    if(this.clientsByPrivateId[privateId]) {
      throw new Error(`Client with id ${privateId} already exists`);
    }

    const webRtcClient = new WebRTCClient(privateId, this.config.options.webRtcOptions || { iceServers: [] });
    
    const options : WebRTCClientOptions = {
      webRtcClient,
    }
   
    const client = {
      options,
      gotFirstAreas: false,
      reqSeq: 0,
      pendingReqs: {},
      disconnect: () => {
        this.logger.debug(`Disconnecting client: ${privateId}`);
        if(options.closed) {
          this.logger.debug(`Client already closed: ${privateId}. Call remove client.`);
          this.removeClient(privateId);
          return;
        }
        this.logger.info(`Closing out client: ${privateId}`);
        options.closed = true;
        cleanupTimers();
        webRtcClient.removeListener('channel-open', handleChannelOpen);
        webRtcClient.removeListener('channel-message', handleChannelMessage);
        webRtcClient.removeListener('channel-closed', handleChannelClosed);
        try {
          webRtcClient.close();
          webRtcClient.removeAllListeners('added-signal');
          webRtcClient.removeAllListeners('complete-gathering');
        } catch (err) {
          console.error('Error closing datachannel', err);
        }
        this.removeClient(privateId);
        
        //send disconnect message
      },
      privateId,
      publicId,
      onMessage: (cb: (msg: ProtocolArray) => void) => {
        options.messageHandler = cb;
      },
      send: (msg: string, reliable=false, _ordered=false) => {
        try {
          if (reliable) {
            webRtcClient.sendReliable(msg);
          } else {
            webRtcClient.sendUnreliable(msg);
          }
        } catch (err) {
          this.logger.error(`Error sending WebRTC message to ${privateId}: ${err instanceof Error ? err.message : String(err)}`);
        }
      }
    }
    this.addClient(client);
    
    const cleanupTimers = () => {
      if(options.pingInterval) {
        clearTimeout(options.pingInterval);
        delete options.pingInterval;
      }
      if(options.gatheringTimeout) {
        clearTimeout(options.gatheringTimeout);
        delete options.gatheringTimeout;
      }
      if(options.noPingReceivedTimeout) {
        clearTimeout(options.noPingReceivedTimeout);
        delete options.noPingReceivedTimeout;
      }
    };

    const onFirstPingReceived = () => {
      this.logger.debug(`Received first ping from client: ${privateId}`)
      const { timeout, ws } = this.awaitingInits[privateId];
      clearTimeout(timeout);
      if(client.options.webRtcClient.gatheringComplete) {
        this.logger.debug(`COMPLETED GATHERING on first ping for client: ${client.privateId}`);
        ws.close();
        client.options.webRtcClient.removeAllListeners('added-signal');
      } else {
        client.options.gatheringTimeout = setTimeout(() => {
          ws.close();
          client.options.webRtcClient.removeAllListeners('added-signal');
          client.options.webRtcClient.removeAllListeners('complete-gathering');
        }, 60000);
        client.options.webRtcClient.once('complete-gathering', () => {
          this.logger.debug(`COMPLETED GATHERING for client: ${client.privateId}`);
          clearTimeout(client.options.gatheringTimeout);
          delete client.options.gatheringTimeout;
          ws.close();
          client.options.webRtcClient.removeAllListeners('added-signal');
        })
      }
      delete this.awaitingInits[privateId];
    }
    
    let pingStarted = false;
    let receivedFirstPing = false;

    const startPingLoop = () => {
      if(pingStarted) {
        return;
      }
      pingStarted = true;
      const pingIntervalMs = this.config.options.pingInterval || 1500;
      const sendPing = () => {
        if(options.closed) {
          return;
        }
        try {
          webRtcClient.sendReliable(PING_STRINGED);
        } catch (err) {
          this.logger.warn(`Error sending ping to client ${privateId}: ${err instanceof Error ? err.message : String(err)}`);
        }
        options.pingInterval = setTimeout(sendPing, pingIntervalMs);
      };
      sendPing();
      options.noPingReceivedTimeout = setTimeout(() => {
        client.disconnect();
      }, this.config.options.clientTimeout || DEFAULT_PING_TIMEOUT);
    };

    const handleChannelOpen = (label: string) => {
      if(label === WEBRTC_RELIABLE_CHANNEL) {
        if(!this.awaitingInits[privateId]) {
          throw new Error(`Should be still awaiting for the init of player: ${privateId}`);
        }
        startPingLoop();
      }
    };

    const handleChannelClosed = (label: string) => {
      if(label === WEBRTC_RELIABLE_CHANNEL && !options.closed) {
        this.logger.debug(`Reliable channel closed for client ${privateId}`);
        this.removeClient(privateId);
      }
    };

    const handleChannelMessage = (label: string, dcMsg: string | Buffer | ArrayBuffer) => {
      if(typeof dcMsg !== 'string') {
        this._onRawClientMessage(client, ensureArrayBuffer(dcMsg));
        return;
      }
      const stringedMsg = dcMsg;
      if(!stringedMsg) {
        return;
      }
      if(label === WEBRTC_RELIABLE_CHANNEL && stringedMsg === PING_STRINGED) {
        if(!receivedFirstPing) {
          receivedFirstPing = true;
          onFirstPingReceived();
        }
        if(options.noPingReceivedTimeout) {
          clearTimeout(options.noPingReceivedTimeout);
        }
        options.noPingReceivedTimeout = setTimeout(() => {
          client.disconnect();
        }, this.config.options.clientTimeout || DEFAULT_PING_TIMEOUT);
        return;
      }

      let payload: ProtocolArray;
      try {
        payload = JSON.parse(stringedMsg);
      } catch (err) {
        this.logger.warn(`Failed to parse message from client ${privateId}: ${err instanceof Error ? err.message : String(err)}`);
        return;
      }

      if(!Array.isArray(payload)) {
        this.logger.warn(`Received non-array payload from client ${privateId}: ${stringedMsg}`);
        return;
      }

      if(payload[0] === PROTOCOLS.CLIENT_CLOSE) {
        this.logger.debug(`Handling graceful disconnect from client: ${privateId}`);
        this.removeClient(privateId);
        return;
      }

      options.messageHandler?.(payload);
    };

    webRtcClient.on('channel-open', handleChannelOpen);
    webRtcClient.on('channel-message', handleChannelMessage);
    webRtcClient.on('channel-closed', handleChannelClosed);

    webRtcClient.once('closed-channel', () => {
      this.logger.debug(`Closed channel for client: ${privateId}`);
      if(!options.closed) {
        this.removeClient(privateId);
      }
    });
  }

  public async close() {
    this.logger.info(`Closing WebRTCServerConnector. Disconnecting: ${this.clients.length} clients`);
    const clients = [...this.clients];
    clients.forEach((client) => {
      if(this.clientsByPrivateId[client.privateId]) {
        this.removeClient(client.privateId);
      } else {
        client.disconnect();
      }
    });
    // Close any pending websocket handshakes that never finished initialization.
    Object.keys(this.awaitingInits).forEach((privateId) => {
      const pending = this.awaitingInits[privateId];
      if(!pending) {
        return;
      }
      clearTimeout(pending.timeout);
      try {
        pending.ws.close();
      } catch (err) {
        this.logger.warn(`Error closing pending ws for client ${privateId}: ${err instanceof Error ? err.message : String(err)}`);
      }
      delete this.awaitingInits[privateId];
    });
    this.clients.length = 0;
    return new Promise<void>((resolve, reject) => {
      let settled = false;
      const finish = (err?: Error) => {
        if(settled) {
          return;
        }
        settled = true;
        if(err) {
          console.error(`Error closing server: ${err.message}`);
          reject(err);
        } else {
          delete this.wsServer;
          resolve();
        }
      };
      this.wsServer.close((err) => {
        finish(err ?? undefined);
      });
      setTimeout(() => finish(), 2000);
    });
  }
}
